import React, { useCallback, useMemo, type KeyboardEvent, type MouseEvent } from 'react';
import { ExpandableMenuItem, ExpandableMenuItemContent } from '@atlaskit/navigation-system';
import {
	EnvironmentLozenge,
	nameWithCustomEnvironment,
} from '@atlassian/jira-forge-ui-extension-title/src/ui/environment-lozenge/index.tsx';
import type { MenuItemAnalytics } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/utils/get-analytics-attributes/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { MoreNavMenuButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import ExpandableMenuItemTrigger from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/expandable-menu-item-trigger-with-analytics/index.tsx';
import { useLocation } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/use-location/index.tsx';

import type { ForgeItemProps } from '../../types.tsx';
import { ForgeIcon } from './forge-icon/index.tsx';
import { ForgeItemExpandable } from './forge-item-expandable/index.tsx';
import { ForgeItemSimple } from './forge-item-simple/index.tsx';

export const getForgeItemAnalytics = (
	analytics: ForgeItemProps['analytics'],
	itemId: ForgeItemProps['id'],
): MenuItemAnalytics => {
	return {
		...analytics,
		// @ts-expect-error - itemType is not in the type
		itemType: 'app',
		componentType: 'customItem',
		itemId,
	};
};

export const ForgeItem = (props: ForgeItemProps): React.JSX.Element => {
	const {
		id: itemId,
		url,
		showIcon,
		iconUrl,
		environmentType,
		environmentKey,
		name,
		sections,
		overrides,
		analytics,
		HideFromSidebarButton,
	} = props;

	const location = useLocation();

	const shouldExpandAsDefault = useMemo(() => location.pathname.includes(url), [url, location]);

	const onNestedMenuItemClick = useCallback(
		(event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
			if (overrides?.ParentMenuItem?.onClick) {
				overrides.ParentMenuItem.onClick(
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					event as MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
				);
			}
			return () => null;
		},
		[overrides],
	);

	const itemAnalytics = useMemo<MenuItemAnalytics>(() => {
		return getForgeItemAnalytics(analytics, itemId);
	}, [analytics, itemId]);

	const expandableItemAnalytics = useMemo<MenuItemAnalytics>(() => {
		return {
			...itemAnalytics,
			level: 2,
			itemType: 'appSubpage',
			parentItemType: 'app',
		};
	}, [itemAnalytics]);

	if (!sections?.length) {
		return <ForgeItemSimple {...props} analytics={itemAnalytics} />;
	}

	if (overrides?.showSelectedAppMenuOnly) {
		return <ForgeItemExpandable {...props} analytics={itemAnalytics} />;
	}

	return (
		<ExpandableMenuItem isDefaultExpanded={shouldExpandAsDefault}>
			<ExpandableMenuItemTrigger
				elemBefore={showIcon ? <ForgeIcon url={iconUrl} /> : undefined}
				elemAfter={<EnvironmentLozenge environmentType={environmentType} />}
				testId={`navigation-sidebar.project-menu.forge-app.${itemId}.expandable-menu-item-trigger.root`}
				analytics={itemAnalytics}
				onClick={onNestedMenuItemClick}
				interactionName="nav4-sidebar-forge-item"
				{...(fg('more_for_nav_4_level_3')
					? {
							actionsOnHover: HideFromSidebarButton && (
								<MoreNavMenuButton MenuItems={HideFromSidebarButton} name={name} />
							),
						}
					: {})}
			>
				{nameWithCustomEnvironment(name, environmentType, environmentKey)}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<ForgeItemExpandable {...props} analytics={expandableItemAnalytics} />
			</ExpandableMenuItemContent>
		</ExpandableMenuItem>
	);
};
